// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ProjectFulfillmentBySkuRow } from '@/project/api/types';
import { useProjectContext } from '@/project/hooks/use-project-context';
import { useProjectFulfillmentBySku } from '@/project/hooks/use-project-fulfillment-by-sku';
import { formatNumber } from '@/utilities/number';
import {
  Box,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Suspense } from 'react';

/**
 * Summary view for the project Fulfillment tab. Renders one row per SKU,
 * joining project-level max-concurrent demand with FO quantities aggregated
 * across every non-cancelled FO on the project.
 */
export function FulfillmentBySkuCard() {
  return (
    <Suspense fallback={<SkuSummaryTableSkeleton />}>
      <FulfillmentBySkuCardContent />
    </Suspense>
  );
}

function FulfillmentBySkuCardContent() {
  const { project } = useProjectContext();
  const { rows } = useProjectFulfillmentBySku(project.key);

  if (rows.length === 0) {
    return (
      <Paper
        variant="outlined"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 4,
          minHeight: 160,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          No SKUs to summarize yet.
        </Typography>
      </Paper>
    );
  }

  return (
    <Stack spacing={1}>
      <SkuSummaryTable rows={rows} />
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ paddingX: 2, paddingTop: 0.5 }}
      >
        Quantities reflect fulfillment orders only. Subsequent returns, scraps,
        and other post-fulfillment changes are not reported here.
      </Typography>
    </Stack>
  );
}

interface SkuSummaryTableProps {
  rows: ProjectFulfillmentBySkuRow[];
}

function SkuSummaryTable({ rows }: SkuSummaryTableProps) {
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>SKU</TableCell>
            <TableCell align="right">Project requested</TableCell>
            <TableCell align="right">FO requested</TableCell>
            <TableCell align="right">Pending</TableCell>
            <TableCell align="right">Shipped</TableCell>
            <TableCell align="right">Delivered</TableCell>
            <TableCell align="right">Need (overage)</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.sku}>
              <TableCell>
                <Typography variant="body2">
                  {row.sku}
                  {row.itemName && (
                    <Typography
                      component="span"
                      variant="body2"
                      color="text.secondary"
                    >
                      {' '}
                      ({row.itemName})
                    </Typography>
                  )}
                </Typography>
              </TableCell>
              <TableCell align="right">
                {formatNumber(row.projectRequested)}
              </TableCell>
              <TableCell align="right">
                {formatNumber(row.foRequested)}
              </TableCell>
              <TableCell align="right">{formatNumber(row.pending)}</TableCell>
              <TableCell align="right">{formatNumber(row.shipped)}</TableCell>
              <TableCell align="right">{formatNumber(row.delivered)}</TableCell>
              <TableCell align="right">
                {formatNumber(row.netNeedQuantity, { accounting: true })}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function SkuSummaryTableSkeleton() {
  return (
    <Box>
      <Stack spacing={1}>
        <Skeleton variant="rectangular" height={36} />
        <Skeleton variant="rectangular" height={36} />
        <Skeleton variant="rectangular" height={36} />
      </Stack>
    </Box>
  );
}
